import { useCallback, useEffect, useMemo, useState } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { FileTypeNumber } from "../../../core/api/requests/file/download/types";
import { appActions } from "../../../ducks/app";
import { authSelectors } from "../../../ducks/auth";
import { dataActions } from "../../../ducks/data";
import {
  fileActions,
  fileSelectors,
  FileUploadResult,
} from "../../../ducks/file";
import { UserRole } from "../../../ducks/user";
import {
  TableHeaderItem,
  TablePagination,
  TableSortBy,
} from "../../../hooks/useTable";
import { AppDispatch } from "../../../store";
import { formatDateTime } from "../../../utils/date";
import { FileStatus } from "../../molecules/file_upload/FileStatus";

export const useFileUploadResult = () => {
  // * Hooks
  const dispatch = useDispatch<AppDispatch>();

  // * Selectors
  const results = useSelector(fileSelectors.results);
  const loggedInUser = useSelector(authSelectors.loggedInUser);

  // * Effects
  useEffect(() => {
    dispatch(fileActions.getFileUploadTemplates.started({}));
    dispatch(dataActions.getMenus.started({}));
    dispatch(dataActions.getRegistrationResults.started({}));

    if (loggedInUser?.role === UserRole.User) {
      return;
    }
    dispatch(
      fileActions.getFileUploadUsers.started({
        sort_key: "last_name",
        sort_order: "asc",
      }),
    );
  }, [loggedInUser]);

  useEffect(() => {
    setRowResults(results.data);
  }, [results]);

  // * State
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [rowResults, setRowResults] = useState<FileUploadResult[]>(
    results.data,
  );
  const [methods, setMethods] =
    useState<UseFormReturn<FieldValues, unknown, FieldValues>>();

  const [filter, setFilter] = useState<{
    fileName?: string;
    userId?: string;
    menuId?: number;
    templateId?: number;
    fromDate?: string;
    toDate?: string;
    status?: number;
  }>({
    fileName: undefined,
    userId: undefined,
    menuId: undefined,
    templateId: undefined,
    fromDate: undefined,
    toDate: undefined,
    status: undefined,
  });

  // * Memo
  const tableHeaders: TableHeaderItem<FileUploadResult>[] = useMemo(() => {
    return [
      {
        id: 1,
        title: "ファイル名",
        popupTitle: "ファイル名",
        dataKey: "fileName",
        sortKey: "file_name",
        width: "30%",
      },
      {
        id: 2,
        title: "サイズ",
        popupTitle: "サイズ",
        dataKey: "fileSize",
        sortKey: "file_size",
      },
      {
        id: 3,
        title: "実施ユーザー",
        popupTitle: "実施ユーザー",
        dataKey: "userName",
        sortKey: "accounts.last_name",
        width: "30%",
      },
      {
        id: 4,
        title: "メニュー名",
        dataKey: "menuName",
        sortKey: "menu_id",
        width: "30%",
      },
      {
        id: 5,
        title: "テンプレート",
        popupTitle: "テンプレート",
        dataKey: "templateName",
        sortKey: "templates.template_name",
        width: "30%",
      },
      {
        id: 6,
        title: "実施日時",
        popupTitle: "実施日時",
        dataKey: "uploadDate",
        dataMapFunction: ({ uploadDate }) =>
          formatDateTime(uploadDate, false, "YYYY/MM/DD HH:mm"),
        sortKey: "upload_datetime",
      },
      {
        id: 7,
        title: "登録結果",
        popupTitle: "登録結果",
        dataKey: "result",
        dataMapFunction: ({ result }) => ({ status: result }),
        customComponent: FileStatus,
        sortKey: "result",
      },
    ];
  }, []);

  const total = useMemo(() => results.total, [results]);

  // * Handlers

  // ** Memorize Utility Function
  const transformFilter = useCallback(
    (filter: {
      fileName?: string;
      userId?: string;
      menuId?: number;
      templateId?: number;
      fromDate?: string;
      toDate?: string;
      status?: number;
    }) => {
      return {
        mode: 1,
        file_name: filter.fileName,
        user_id: filter.userId,
        menu_id: filter.menuId,
        template_id: filter.templateId,
        date_from: filter.fromDate
          ? formatDateTime(filter.fromDate, false, "YYYY-MM-DDThh:mm:ssZ")
          : filter.fromDate,
        date_to: filter.toDate
          ? formatDateTime(filter.toDate, false, "YYYY-MM-DDThh:mm:ssZ")
          : filter.toDate,
        result: filter.status,
      };
    },
    [],
  );

  // ** Handler Function

  const fetchFileUploadResult = useCallback(
    (sort: TableSortBy, pagination?: TablePagination) => {
      dispatch(appActions.showLoading());
      const data = transformFilter(filter);
      dispatch(
        fileActions.getFileUploadResults.started({
          mode: data.mode,
          file_name: data.file_name,
          user_id: data.user_id,
          menu_id: data.menu_id,
          template_id: data.template_id,
          date_from: data.date_from,
          date_to: data.date_to,
          result: data.result,
          sort_key: sort.field,
          sort_order: sort.order,
          page: pagination?.page ?? 1,
          size: pagination?.size ?? 100,
          onComplete: () => dispatch(appActions.hideLoading()),
        }),
      );
    },
    [filter],
  );

  const handleFilter = useCallback(
    (payload: {
      fileName?: string;
      userId?: string;
      menuId?: number;
      templateId?: number;
      fromDate?: string;
      toDate?: string;
      status?: number;
    }) => {
      setFilter((prev) => ({
        ...prev,
        ...payload,
      }));
      setShowFilter(false);
      dispatch(appActions.showLoading());
      const data = transformFilter(payload);
      dispatch(
        fileActions.getFileUploadResults.started({
          mode: data.mode,
          file_name: data.file_name,
          user_id: data.user_id,
          menu_id: data.menu_id,
          template_id: data.template_id,
          date_from: data.date_from,
          date_to: data.date_to,
          result: data.result,
          sort_key: "upload_datetime",
          sort_order: "desc",
          page: 1,
          size: 100,
          onComplete: () => dispatch(appActions.hideLoading()),
        }),
      );
    },
    [],
  );

  const handleRemoveFilterBadge = useCallback(
    ({ value, key }: { value?: string | number; key: string }) => {
      if (!methods) return;

      const updatedFilter = { ...filter, [key]: undefined };
      if (key === "dateRange") {
        updatedFilter.fromDate = undefined;
        updatedFilter.toDate = undefined;
        methods.setValue("fromDate", undefined);
        methods.setValue("toDate", undefined);
      } else {
        methods.setValue(key, undefined);
      }

      setFilter(updatedFilter);
      dispatch(appActions.showLoading());
      const data = transformFilter(updatedFilter);
      dispatch(
        fileActions.getFileUploadResults.started({
          mode: data.mode,
          file_name: data.file_name,
          user_id: data.user_id,
          menu_id: data.menu_id,
          template_id: data.template_id,
          date_from: data.date_from,
          date_to: data.date_to,
          result: data.result,
          sort_key: "upload_datetime",
          sort_order: "desc",
          page: 1,
          size: 100,
          onComplete: () => dispatch(appActions.hideLoading()),
        }),
      );
    },
    [filter, methods],
  );

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      setRowResults(
        results.data.map((result) => ({
          ...result,
          selected: checked,
        })),
      );
    },
    [results],
  );

  const handleRowSelect = useCallback(
    (data: FileUploadResult, checked: boolean) => {
      setRowResults((prev) =>
        prev.map((row) => (row === data ? { ...row, selected: checked } : row)),
      );
    },
    [],
  );

  const handleRowDownload = useCallback(
    (data: FileUploadResult, fileType: FileTypeNumber) => {
      dispatch(appActions.showLoading());
      dispatch(
        fileActions.downloadFileUploadResult.started({
          upload_result_id: data.id,
          file_type: fileType,
          onComplete: () => dispatch(appActions.hideLoading()),
          onSuccess: ({ data }) => window.open(data.download_url, "_blank"),
        }),
      );
    },
    [],
  );

  const handleRowDelete = useCallback((data: FileUploadResult) => {
    dispatch(
      appActions.showModal({
        title: "アップロード結果削除",
        buttons: [
          {
            text: "キャンセル",
            variant: "dark",
            onClick: () => dispatch(appActions.hideModal()),
          },
          {
            text: "削除",
            variant: "filled",
            onClick: () => {
              dispatch(appActions.hideModal());
              dispatch(appActions.showLoading());
              dispatch(
                fileActions.deleteFileUploadResults.started({
                  upload_result_id: [data.id],
                  onSuccess: () =>
                    fetchFileUploadResult({
                      field: "upload_datetime",
                      order: "desc",
                    }),
                  onComplete: () => dispatch(appActions.hideLoading()),
                }),
              );
            },
          },
        ],
      }),
    );
  }, []);

  const handleBulkDelete = useCallback(() => {
    const uploadResultIds = rowResults
      .filter((result) => result.selected)
      .map((result) => result.id);
    if (!uploadResultIds.length) return;

    dispatch(
      appActions.showModal({
        title: "アップロード結果削除",
        buttons: [
          {
            text: "キャンセル",
            variant: "dark",
            onClick: () => dispatch(appActions.hideModal()),
          },
          {
            text: "削除",
            variant: "filled",
            onClick: () => {
              dispatch(appActions.hideModal());
              dispatch(appActions.showLoading());
              dispatch(
                fileActions.deleteFileUploadResults.started({
                  upload_result_id: uploadResultIds,
                  onSuccess: () =>
                    fetchFileUploadResult({
                      field: "upload_datetime",
                      order: "desc",
                    }),
                  onComplete: () => dispatch(appActions.hideLoading()),
                }),
              );
            },
          },
        ],
      }),
    );
  }, [rowResults]);

  const handleMethodsCallback = useCallback(
    (methods: UseFormReturn<FieldValues, unknown, FieldValues>) => {
      setMethods(methods);
    },
    [],
  );

  return {
    results: rowResults,
    total,
    tableHeaders,
    showFilter,
    filter,
    fetchFileUploadResult,
    setShowFilter,
    handleFilter,
    handleBulkDelete,
    handleRowDelete,
    handleRowDownload,
    handleSelectAll,
    handleRowSelect,
    handleRemoveFilterBadge,
    handleMethodsCallback,
  };
};
